import { Link } from 'react-router-dom';
import { useLanguage } from '@/hooks/useLanguage';
import { Article, getEnglishContent } from '@/types';
import { getCategoryById } from '@/data/categories';
import { cn } from '@/lib/utils';
import { optimizeImageUrl, getFallbackImage } from '@/lib/imageUtils';

interface ArticleCardProps {
  article: Article;
  index?: number;
}

export const ArticleCard = ({ article, index = 0 }: ArticleCardProps) => {
  const { t } = useLanguage();
  const content = getEnglishContent(article);
  const category = getCategoryById(article.category);
  const categoryName = category?.name || article.category;

  const formattedDate = new Date(article.publishedAt).toLocaleDateString('en', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // Estimate reading time from excerpt length (~200 wpm, proxy from excerpt)
  const wordCount = (content.excerpt?.split(/\s+/).length || 0) * 40; // excerpt ≈ 1/40th of body
  const readingTime = Math.max(4, Math.min(12, Math.round(wordCount / 200)));

  return (
    <Link
      to={`/article/${article.slug}`}
      className={cn(
        'group relative block overflow-hidden bg-background border border-border/40',
        'transition-all duration-700 hover:border-primary/30',
        'animate-fade-in'
      )}
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      {/* Top decorative line */}
      <div className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />

      {/* Image */}
      <div className="relative overflow-hidden" style={{ aspectRatio: '16/10' }}>
        <img
          src={optimizeImageUrl(article.image, 800)}
          srcSet={`${optimizeImageUrl(article.image, 400)} 400w, ${optimizeImageUrl(article.image, 600)} 600w, ${optimizeImageUrl(article.image, 800)} 800w`}
          sizes="(max-width: 768px) 90vw, (max-width: 1024px) 45vw, 25vw"
          alt={content.title}
          loading="lazy"
          decoding="async"
          width={800}
          height={500}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          onError={(e) => { (e.target as HTMLImageElement).src = getFallbackImage(article.category); }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent opacity-40" />
        
        {/* Category badge - outline style */}
        <span className="absolute bottom-4 left-4 px-3 py-1.5 border border-accent/80 bg-background/90 text-accent text-[10px] tracking-luxury uppercase font-normal">
          {categoryName}
        </span>
        {/* Reading time */}
        <span className="absolute bottom-4 right-4 px-2 py-1 bg-background/90 text-muted-foreground text-[10px] tracking-luxury uppercase font-normal">
          {readingTime} min read
        </span>
      </div>

      {/* Content */}
      <div className="p-6 md:p-8">
        {/* Date */}
        <p className="text-[10px] tracking-luxury uppercase text-muted-foreground/70 mb-4">
          {formattedDate}
        </p>
        
        {/* Title */}
        <h3 className="font-serif text-xl md:text-2xl font-medium text-foreground mb-4 group-hover:text-primary transition-colors duration-500 line-clamp-2 leading-tight">
          {content.title}
        </h3>
        
        {/* Excerpt */}
        <p className="text-sm text-muted-foreground/80 line-clamp-2 mb-6 font-light leading-relaxed">
          {content.excerpt}
        </p>
        
        {/* Read more */}
        <span className="inline-flex items-center gap-2 text-[10px] tracking-luxury uppercase text-accent/80 group-hover:text-accent transition-colors duration-500">
          {t('home.readMore')}
          <span className="transform group-hover:translate-x-1 transition-transform duration-500">→</span>
        </span>
      </div>
    </Link>
  );
};
